import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  Plus, Tag, Percent, DollarSign, Trash2, Pencil, Copy,
  Zap, Calendar, Gift, Users, ShoppingCart
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const PROMO_TYPES = [
  { value: 'percentage', label: 'Percentage Off', icon: Percent },
  { value: 'fixed_amount', label: 'Fixed Amount Off', icon: DollarSign },
  { value: 'buy_x_get_y', label: 'Buy X Get Y', icon: Gift },
  { value: 'free_shipping', label: 'Free Shipping', icon: ShoppingCart },
  { value: 'bundle_deal', label: 'Bundle Deal', icon: Zap },
  { value: 'flash_sale', label: 'Flash Sale', icon: Zap },
  { value: 'first_purchase', label: 'First Purchase', icon: Users },
  { value: 'seasonal', label: 'Seasonal', icon: Calendar },
  { value: 'referral_reward', label: 'Referral Reward', icon: Users },
];

function PromoModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({
    name: '', description: '', type: 'percentage', code: '',
    discount_value: '', min_purchase: '', max_uses: '', start_date: '', end_date: '', active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || '', description: editing.description || '',
        type: editing.type || 'percentage', code: editing.code || '',
        discount_value: editing.discount_value?.toString() || '',
        min_purchase: editing.min_purchase?.toString() || '',
        max_uses: editing.max_uses?.toString() || '',
        start_date: editing.start_date || '', end_date: editing.end_date || '',
        active: editing.active !== false
      });
    } else {
      setForm({ name: '', description: '', type: 'percentage', code: '', discount_value: '', min_purchase: '', max_uses: '', start_date: '', end_date: '', active: true });
    }
  }, [editing, open]);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
    setForm(p => ({...p, code}));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Promotion name required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value) || 0,
        min_purchase: parseFloat(form.min_purchase) || 0,
        max_uses: parseInt(form.max_uses) || 0,
      };
      if (editing) {
        const { data } = await axios.put(`${API}/api/sales/promotions/${editing.id}`, payload);
        toast.success('Promotion updated');
        onSaved(data, true);
      } else {
        const { data } = await axios.post(`${API}/api/sales/promotions`, payload);
        toast.success('Promotion created');
        onSaved(data, false);
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tag className="h-5 w-5 text-purple-500" />{editing ? 'Edit' : 'Create'} Promotion</DialogTitle>
          <DialogDescription>Set up discount codes and deals</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Promotion Name *</Label>
            <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Spring Sale 2026" data-testid="promo-name-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="20% off all services" rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({...p, type: v}))}>
                <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROMO_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Discount Value</Label>
              <Input type="number" min="0" value={form.discount_value} onChange={e => setForm(p => ({...p, discount_value: e.target.value}))} placeholder={form.type === 'percentage' ? '20' : '50.00'} data-testid="promo-value-input" />
            </div>
          </div>
          <div>
            <Label>Promo Code</Label>
            <div className="flex gap-2">
              <Input value={form.code} onChange={e => setForm(p => ({...p, code: e.target.value.toUpperCase()}))} placeholder="SPRING20" className="flex-1" data-testid="promo-code-input" />
              <Button type="button" variant="outline" onClick={generateCode} className="text-xs gap-1"><Zap className="h-3 w-3" /> Generate</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Min Purchase ($)</Label>
              <Input type="number" min="0" value={form.min_purchase} onChange={e => setForm(p => ({...p, min_purchase: e.target.value}))} placeholder="0" />
            </div>
            <div>
              <Label>Max Uses (0 = unlimited)</Label>
              <Input type="number" min="0" value={form.max_uses} onChange={e => setForm(p => ({...p, max_uses: e.target.value}))} placeholder="0" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} />
            </div>
            <div>
              <Label>End Date</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm(p => ({...p, end_date: e.target.value}))} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} />
            <Label>Active</Label>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-promo-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Create Promotion'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PromotionsPage() {
  const { format: fc, symbol: currSymbol } = useCurrency();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchPromos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sales/promotions`);
      setPromos(data);
    } catch { toast.error('Failed to load promotions'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPromos(); }, [fetchPromos]);

  const handleSaved = (promo, isUpdate) => {
    if (isUpdate) setPromos(prev => prev.map(p => p.id === promo.id ? promo : p));
    else setPromos(prev => [promo, ...prev]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await axios.delete(`${API}/api/sales/promotions/${id}`);
      setPromos(prev => prev.filter(p => p.id !== id));
      toast.success('Promotion deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success('Code copied!'); };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="promotions-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0"><Tag className="h-5 w-5 text-purple-500" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Promotions & Deals<PageInfoTooltip pageKey="promotions" /></h1>
            <p className="text-xs text-gray-400">{promos.length} promotions · {promos.filter(p => p.active).length} active</p>
          </div>
        </div>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-purple-600 hover:bg-purple-700 h-9 gap-1.5" data-testid="create-promo-btn"><Plus className="h-4 w-4" /> New Promotion</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" /></div>
      ) : promos.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-10 text-center">
            <Tag className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No promotions yet</h3>
            <p className="text-sm text-gray-500 mb-4">Create discount codes and deals for your customers</p>
            <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-purple-600 hover:bg-purple-700 gap-1.5"><Plus className="h-4 w-4" /> New Promotion</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {promos.map(p => {
            const typeInfo = PROMO_TYPES.find(t => t.value === p.type) || PROMO_TYPES[0];
            const TypeIcon = typeInfo.icon;
            return (
              <Card key={p.id} className="border-gray-200 hover:shadow-md transition-shadow" data-testid={`promo-card-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-bold text-gray-900 truncate">{p.name}</h3>
                        <Badge className={p.active ? 'bg-green-100 text-green-700 text-[9px]' : 'bg-gray-100 text-gray-500 text-[9px]'}>{p.active ? 'Active' : 'Inactive'}</Badge>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <TypeIcon className="h-3 w-3 text-purple-500" />
                        <span className="text-[10px] text-gray-500">{typeInfo.label}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-purple-600">
                        {p.type === 'percentage' ? `${p.discount_value}%` : `${currSymbol}${p.discount_value}`}
                      </p>
                      <span className="text-[9px] text-gray-400">off</span>
                    </div>
                  </div>

                  {p.description && <p className="text-xs text-gray-500 line-clamp-2 mb-2">{p.description}</p>}

                  {p.code && (
                    <div className="flex items-center gap-2 bg-purple-50 rounded-md px-3 py-1.5 mb-2">
                      <Tag className="h-3 w-3 text-purple-400" />
                      <code className="text-xs font-mono font-bold text-purple-700 flex-1">{p.code}</code>
                      <button onClick={() => copyCode(p.code)} className="text-purple-400 hover:text-purple-600" data-testid={`copy-code-${p.id}`}><Copy className="h-3 w-3" /></button>
                    </div>
                  )}

                  <div className="flex items-center gap-3 text-[10px] text-gray-400 mb-3 flex-wrap">
                    {p.min_purchase > 0 && <span>Min: ${p.min_purchase}</span>}
                    {p.max_uses > 0 && <span>Limit: {p.max_uses} uses</span>}
                    {p.used_count > 0 && <span className="text-emerald-600">Used: {p.used_count}x</span>}
                    {p.start_date && <span>From: {p.start_date}</span>}
                    {p.end_date && <span>Until: {p.end_date}</span>}
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={() => { setEditing(p); setModalOpen(true); }} data-testid={`edit-promo-${p.id}`}><Pencil className="h-3 w-3" /> Edit</Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(p.id)} data-testid={`delete-promo-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <PromoModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
    </div>
  );
}
